import {ClickEvent, DataSourceMode, DataSourceRemoteDataChangeEvent, ModelDataChangeType, SelectionType, Tab, Table, TableRow} from "@mcleod/components";
import {LogManager, ModelRow, StringUtil, UrlUtil} from "@mcleod/core";
import {AutogenLayoutMobileCommList} from "./autogen/AutogenLayoutMobileCommList";

const log = LogManager.getLogger("lme.dispatch.mobilecomm.MobileCommList");

export interface MobileCommUrlProps {
    key?: string;
    tab: number;
    id: string;
}

export class MobileCommList extends AutogenLayoutMobileCommList {

    private static readonly SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

    private _unfilteredData: ModelRow[];
    private props: MobileCommUrlProps;

    async onLoad() {
        this.addLayoutLoadListener(event => this.searchList());
        this.setupBeforeLookupModelSearchListeners();
        this.sourceMcMessagePosition.orderBy = [{field: "message_send_date", sort: "desc"}];
    }

    private applyPreSearchProps() {
        this.props = UrlUtil.getPropsFromUrl(window.location.href);
        if (this.props == null) return;

        if (this.props.tab) {
            this.tabsetMobileCommList.getTab(this.props.tab).select();
        }
        const table = this.tabsetMobileCommList.getActiveTab().findComponentByType(Table);
        table.addFilterChangedListener(() => this.applyPostSearchProps());
        if (this.props.key) {
            table.setFilter(this.props.key);
        }
    }

    private applyPostSearchProps() {
        if (!this.props || !this.props.id) return;
        const table = this.tabsetMobileCommList.getActiveTab().findComponentByType(Table);
        table.allRows.filter(
            row => row.data.get("id") == this.props.id
        ).forEach(
            (row: TableRow) => {
                table.selectRow(row, SelectionType.SINGLE, null);
                table.scrollToSelection();
            }
        );
    }

    searchList() {
        this.applyPreSearchProps();
        this.sourceMcMessagePosition.search().then(() => {
            this.unfilteredData = this.sourceMcMessagePosition.data
            this.applyPostSearchProps();
        });
    }

    setupBeforeLookupModelSearchListeners() {
        this.textboxDriver.addBeforeLookupModelSearchListener((event) => event.filter.is_active = 'Y');
        this.textboxDriverManager.addBeforeLookupModelSearchListener((event) => event.filter.is_active = 'Y');
    }

    filterData() {
        const orderId = this.textboxOrder.text;
        const driverId = this.textboxDriver.getFirstLookupModelData()?.get("id");
        const driverManager = this.textboxDriverManager.getFirstLookupModelData()?.get("id");
        this.sourceMcMessagePosition.data = this._unfilteredData;

        const filteredData = this.sourceMcMessagePosition.data
            .filter((row) => StringUtil.isEmptyString(orderId) || row.data["order_id"] == orderId)
            .filter((row) => StringUtil.isEmptyString(driverId) || row.data["driver_id"] == driverId)
            .filter((row) => StringUtil.isEmptyString(driverManager) || row.data["driver_manager"] == driverManager);

        const dataAll = filteredData;
        const dataMessages = [];
        const dataError = [];

        filteredData.forEach((row) => {
            if (row.data["row_type"] == 'M') {
                dataMessages.push(row);
            }
            if (row.data["message_text"]
                && (row.data["message_text"] as string).toLowerCase().startsWith("external application error")) {
                dataError.push(row);
            }
        });

        [{layout:this.layoutAll, data: dataAll},
         {layout:this.layoutMessages, data: dataMessages},
         {layout:this.layoutErrors, data: dataError}
        ].forEach(({layout, data}, i) => {
            layout.sourceMcMessagePosition.orderBy = this.sourceMcMessagePosition.orderBy;
            layout.sourceMcMessagePosition.setRowsAndMode(DataSourceMode.NONE, data);
            const tab: Tab = this.tabsetMobileCommList.getTab(i);
            const captionTokens = tab.caption.split(' ')
            // If last token is a number, new data length should replace it, else append new length
            const captionLastTokenIsNaN = isNaN(+captionTokens.pop());
            tab.caption = captionLastTokenIsNaN
                ? `${tab.caption} ${data.length}`
                : `${captionTokens.join(' ')} ${data.length}`
        });
    }

    sourceMcMessagePositionOnRemoteDataChange(event: DataSourceRemoteDataChangeEvent) {
        let changeType = event.change.type as ModelDataChangeType;
        const changeData = event.change.data as ModelRow;

        const msgSendingStatus = changeData.get("msg_sending_status");
        const messageSendDate = changeData.get("message_send_date");
        const isCurrent = changeData.get("is_current");
        const now = new Date();
        const sevenDaysAgo = new Date(now.getTime() - MobileCommList.SEVEN_DAYS_MS);
        const sendDate = messageSendDate ? new Date(messageSendDate) : null;
        const isOutsideDateRange = sendDate == null || sendDate < sevenDaysAgo;
        changeData.getMetadata().keyFields = ["id"];

        if (msgSendingStatus == 200 || isOutsideDateRange || !isCurrent) {
            changeType = ModelDataChangeType.DELETE;
        }

        this.layoutAll.sourceMcMessagePosition.handleDataChange({
            type: changeType,
            data: changeData,
            lastUpdateNanos: event.change.lastUpdateNanos
        });
        this.unfilteredData = this.layoutAll.sourceMcMessagePosition.data;

        event.preventDefault();
    }

    /** This is an event handler for the onClick event of buttonCancel.  */
    buttonSearchOnClick(event: ClickEvent) {
        this.filterData();
    }

    public get unfilteredData(): ModelRow<any>[] {
        return this._unfilteredData;
    }

    public set unfilteredData(newData: ModelRow<any>[]) {
        this._unfilteredData = newData;
        this.filterData();
    }
}
